import React from 'react';
import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import { Checkbox as MUICheckbox } from '@mui/material';
import { get } from 'lodash';
import { cn } from '@src/utils/ui';

const isObject = val => typeof val === 'object' && val !== null;
const isSameOption = (a, b) => {
    if (isObject(a) && isObject(b)) {
        return a.id && b.id && a.id === b.id;
    }
    return a === b;
};

const filterOutOption = (list, option) => list.filter(v => !isSameOption(v, option));

export const Checkbox = ({
    id,
    name,
    value,
    by,
    label,
    className,
    checked,
    onChange,
    size = 'medium',
    checkboxClassName,
    labelClassName,
    labelPosition = 'right'
}) => {
    const { control } = useFormContext();
    const isControlledExternally = typeof checked === 'boolean';
    const checkboxId = id;

    const renderCheckbox = (checkedValue, handleChange, ref) => (
        <label
            htmlFor={checkboxId}
            className={cn(
                'tw-cursor-pointer tw-flex tw-items-center tw-ml-[-10px]',
                labelPosition === 'left' && 'tw-flex-row-reverse',
                className
            )}
        >
            <MUICheckbox
                id={checkboxId}
                size={size}
                checked={checkedValue}
                onChange={handleChange}
                inputRef={ref}
                className={cn(
                    'tw-border-2 tw-border-primary-500 tw-text-primary-500 checked:tw-border-primary',
                    checkboxClassName
                )}
            />
            <span className={cn('tw-w-full', labelClassName)}>{label}</span>
        </label>
    );

    return isControlledExternally ? (
        renderCheckbox(checked, e => onChange?.(e.target.checked))
    ) : (
        <Controller
            name={name}
            control={control}
            render={({ field }) =>
                renderCheckbox(
                    by ? get(field.value, by) === get(value, by) : field.value === value,
                    e => field.onChange(e.target.checked ? value : null),
                    field.ref
                )
            }
        />
    );
};

Checkbox.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    value: PropTypes.any.isRequired,
    by: PropTypes.string,
    label: PropTypes.node.isRequired,
    className: PropTypes.string,
    checked: PropTypes.bool,
    onChange: PropTypes.func,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    checkboxClassName: PropTypes.string,
    labelClassName: PropTypes.string,
    labelPosition: PropTypes.oneOf(['left', 'right'])
};

export const CheckboxGroup = ({
    name,
    options,
    getLabel,
    getValue,
    size = 'medium',
    className,
    checkboxClassName,
    labelClassName,
    labelPosition = 'right'
}) => {
    const { control } = useFormContext();

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={[]}
            render={({ field }) => (
                <div className={className}>
                    {options.map(option => {
                        const optionValue = getValue(option);
                        const isChecked = Array.isArray(field.value)
                            ? field.value.some(v => isSameOption(v, optionValue))
                            : false;

                        const handleChange = checked => {
                            const current = field.value || [];
                            const nextValue = checked
                                ? [...current, optionValue]
                                : filterOutOption(current, optionValue);

                            field.onChange(nextValue);
                        };

                        const key = isObject(optionValue) ? optionValue.id : optionValue;
                        return (
                            <Checkbox
                                key={key}
                                id={`checkbox-${name}-${key}`}
                                name={name}
                                value={optionValue}
                                checked={isChecked}
                                onChange={handleChange}
                                label={getLabel(option)}
                                size={size}
                                checkboxClassName={checkboxClassName}
                                labelClassName={labelClassName}
                                labelPosition={labelPosition}
                            />
                        );
                    })}
                </div>
            )}
        />
    );
};

CheckboxGroup.propTypes = {
    name: PropTypes.string.isRequired,
    options: PropTypes.array.isRequired,
    getLabel: PropTypes.func.isRequired,
    getValue: PropTypes.func.isRequired,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    className: PropTypes.string,
    checkboxClassName: PropTypes.string,
    labelClassName: PropTypes.string,
    labelPosition: PropTypes.oneOf(['left', 'right'])
};
